import { useEffect, useState } from "react";
import {
  ApprovalStatus,
  MeetingAdminResponse,
  MeetingLimitedResponse,
  MeetingsService,
  MeetingStudentAdminResponse,
  MeetingStudentBillingStatus,
  MeetingStudentLimitedResponse,
  MeetingTutorAdminResponse,
  MeetingTutorLimitedResponse,
  MeetingTutorPaymentStatus,
} from "client/openapi";
import {
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import RadioInput from "components/Inputs/RadioInput";
import SuccessDialog from "components/SuccessDialog";
import Notifications from "util/notifications";
import { announceMeetingsChanged } from "util/meetingsChanged";
import { PageStatus } from "types";
import { CHANGE_OPTIONS } from "./changeOptions";
import { LockClosedIcon } from "@radix-ui/react-icons";
import moment from "moment";
import CurrencyInput from "react-currency-input-field";

const TIME_FORMAT = "h:mma";

// Same cap as the subject-rate and custom-rate dialogs use for all rates.
const MAX_RATE_DOLLARS = 5000;

const BILLING_STATUS_LABELS: Record<MeetingStudentBillingStatus, string> = {
  [MeetingStudentBillingStatus.STUDENT_BILLED]: "Billed",
  [MeetingStudentBillingStatus.STUDENT_BILLED_FEE]: "Billed (fee)",
  [MeetingStudentBillingStatus.NO_BILLING_APPLIES]: "No billing applies",
  [MeetingStudentBillingStatus.BILLING_QUEUED]: "Billing queued",
  [MeetingStudentBillingStatus.STUDENT_BILLING_FAILED]: "Billing failed",
  [MeetingStudentBillingStatus.STUDENT_NOT_BILLED]: "Will be billed",
};

const PAYMENT_STATUS_LABELS: Record<MeetingTutorPaymentStatus, string> = {
  [MeetingTutorPaymentStatus.TUTOR_PAID]: "Paid",
  [MeetingTutorPaymentStatus.NO_PAYMENT_APPLIES]: "No payment applies",
  [MeetingTutorPaymentStatus.PAYMENT_QUEUED]: "Payment queued",
  [MeetingTutorPaymentStatus.TUTOR_PAYMENT_FAILED]: "Payment failed",
  [MeetingTutorPaymentStatus.TUTOR_NOT_PAID]: "Will be paid",
};

const APPROVAL_OPTIONS: {
  label: string;
  value: ApprovalStatus;
  activeClasses: string;
}[] = [
  {
    label: "Approve",
    value: ApprovalStatus.APPROVED,
    activeClasses: "bg-white shadow-sm text-green-700",
  },
  {
    label: "Deny",
    value: ApprovalStatus.DENIED,
    activeClasses: "bg-white shadow-sm text-red-700",
  },
  {
    label: "Pending",
    value: ApprovalStatus.PENDING,
    activeClasses: "bg-white shadow-sm text-amber-700",
  },
];

function centsToDollarsString(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Validate a typed rate the same way the subject-rate and custom-rate
 * dialogs do. Empty means "not typed yet" and is not an error.
 */
function verifyRateEdit(value: string | undefined): {
  invalid: boolean;
  message?: string;
} {
  if (value === undefined || value === "") {
    return { invalid: false };
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return { invalid: true, message: "Please enter a valid number." };
  }
  if (num > MAX_RATE_DOLLARS) {
    return {
      invalid: true,
      message: "Please enter a value less than or equal to $5000.00",
    };
  }
  if (num < 0) {
    return {
      invalid: true,
      message: "Please enter a value greater than or equal to $0.00",
    };
  }
  return { invalid: false };
}

function parseRateToCents(value: string | undefined): number | undefined {
  if (value === undefined || value === "") {
    return undefined;
  }
  const num = Number(value);
  if (Number.isNaN(num) || num < 0 || num > MAX_RATE_DOLLARS) {
    return undefined;
  }
  return Math.round(num * 100);
}

function formatAmountForMeeting(rateCents: number, duration: number): string {
  return centsToDollarsString(Math.round((rateCents * duration) / 60));
}

export default function MeetingFinancialsDialog({
  meetingId,
  setEvents,
}: {
  meetingId: number;
  setEvents: (e?: string) => Promise<void>;
}) {
  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADING);
  const [meeting, setMeeting] = useState<
    MeetingAdminResponse | MeetingLimitedResponse
  >();

  // Admin approval (saves immediately when switched)
  const [adminApproval, setAdminApproval] = useState<ApprovalStatus>();
  const [savingApproval, setSavingApproval] = useState<boolean>(false);

  // Rate edits (dollars, as typed), invalid flags, and the last values saved
  // to the server (cents), keyed by meeting_student/meeting_tutor id
  const [studentRateInputs, setStudentRateInputs] = useState<
    Record<number, string | undefined>
  >({});
  const [tutorRateInputs, setTutorRateInputs] = useState<
    Record<number, string | undefined>
  >({});
  const [invalidStudentRates, setInvalidStudentRates] = useState<
    Record<number, boolean>
  >({});
  const [invalidTutorRates, setInvalidTutorRates] = useState<
    Record<number, boolean>
  >({});
  const [savedStudentRates, setSavedStudentRates] = useState<
    Record<number, number>
  >({});
  const [savedTutorRates, setSavedTutorRates] = useState<
    Record<number, number>
  >({});

  // Confirmation dialog state
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [changeType, setChangeType] = useState<CHANGE_OPTIONS>(
    CHANGE_OPTIONS.JUST_THIS_SESSION
  );
  const [saveStatus, setSaveStatus] = useState<PageStatus>();

  // The event prop held by the calendar can be stale, so always pull a fresh
  // copy of the meeting (with rates and billing statuses) when opening.
  useEffect(() => {
    MeetingsService.getMeetingById({ meetingId })
      .then((fetchedMeeting) => {
        setMeeting(fetchedMeeting);
        setAdminApproval(fetchedMeeting.reviewed_and_approved_by_admin);

        const allStudents: (
          | MeetingStudentLimitedResponse
          | MeetingStudentAdminResponse
        )[] = fetchedMeeting.students;
        const studentInputs: Record<number, string | undefined> = {};
        const studentRates: Record<number, number> = {};
        allStudents.forEach((meetingStudent) => {
          if ("rate_student_billed_at" in meetingStudent) {
            studentInputs[meetingStudent.id] = centsToDollarsString(
              meetingStudent.rate_student_billed_at
            );
            studentRates[meetingStudent.id] =
              meetingStudent.rate_student_billed_at;
          }
        });
        setStudentRateInputs(studentInputs);
        setSavedStudentRates(studentRates);

        const allTutors: (
          | MeetingTutorLimitedResponse
          | MeetingTutorAdminResponse
        )[] = fetchedMeeting.tutors;
        const tutorInputs: Record<number, string | undefined> = {};
        const tutorRates: Record<number, number> = {};
        allTutors.forEach((meetingTutor) => {
          if ("rate_tutor_paid_at" in meetingTutor) {
            tutorInputs[meetingTutor.id] = centsToDollarsString(
              meetingTutor.rate_tutor_paid_at
            );
            tutorRates[meetingTutor.id] = meetingTutor.rate_tutor_paid_at;
          }
        });
        setTutorRateInputs(tutorInputs);
        setSavedTutorRates(tutorRates);

        setStatus(PageStatus.SUCCESS);
      })
      .catch((error: any) => {
        console.error(`Error (#${error.status}): ${error.message}`);
        Notifications.error("Unable to load meeting billing details");
        setStatus(PageStatus.ERROR);
      });
  }, [meetingId]);

  const locked = meeting?.locked ?? false;

  const meetingStudents = (meeting?.students ?? []).filter(
    (meetingStudent): meetingStudent is MeetingStudentAdminResponse =>
      "rate_student_billed_at" in meetingStudent
  );
  const meetingTutors = (meeting?.tutors ?? []).filter(
    (meetingTutor): meetingTutor is MeetingTutorAdminResponse =>
      "rate_tutor_paid_at" in meetingTutor
  );

  const changedStudents = meetingStudents.filter((meetingStudent) => {
    const cents = parseRateToCents(studentRateInputs[meetingStudent.id]);
    return (
      cents !== undefined && cents !== savedStudentRates[meetingStudent.id]
    );
  });
  const changedTutors = meetingTutors.filter((meetingTutor) => {
    const cents = parseRateToCents(tutorRateInputs[meetingTutor.id]);
    return cents !== undefined && cents !== savedTutorRates[meetingTutor.id];
  });

  const hasInvalidInput =
    Object.values(invalidStudentRates).some(Boolean) ||
    Object.values(invalidTutorRates).some(Boolean);

  const canSave =
    !locked &&
    !hasInvalidInput &&
    (changedStudents.length > 0 || changedTutors.length > 0);

  const meetingName = meeting
    ? meeting.name || meeting.org_subject.name
    : undefined;
  const meetingTimeString = meeting
    ? `${moment(meeting.start).format("ddd, MMM Do")} · ${moment(
        meeting.start
      ).format(TIME_FORMAT)} - ${moment(meeting.start)
        .add(meeting.duration, "minutes")
        .format(TIME_FORMAT)}`
    : undefined;

  async function changeAdminApproval(newStatus: ApprovalStatus) {
    if (!meeting || locked || savingApproval || newStatus === adminApproval) {
      return;
    }

    const previousStatus = adminApproval;
    setAdminApproval(newStatus);
    setSavingApproval(true);

    try {
      await MeetingsService.editMeeting({
        meetingId: meeting.id,
        requestBody: {
          reviewed_and_approved_by_admin: newStatus,
        },
      });
      // No calendar refetch here on purpose (unlike rate saves): refetching
      // remounts the calendar and closes this dialog mid-interaction. The
      // dialog always loads fresh data when opened, and the calendar does
      // not display approval status. Its loaded weeks reload on its next
      // look.
      announceMeetingsChanged();
      Notifications.success("Meeting approval updated!");
    } catch (error: any) {
      setAdminApproval(previousStatus);
      Notifications.error(error?.message || "Error updating meeting approval");
    } finally {
      setSavingApproval(false);
    }
  }

  async function saveRateChanges() {
    if (!meeting || saveStatus === PageStatus.LOADING) {
      return;
    }

    setSaveStatus(PageStatus.LOADING);

    const applyToAllFutureMeetings =
      changeType === CHANGE_OPTIONS.ALL_FUTURE_OCCURRENCES;

    try {
      const response = await MeetingsService.updateMeetingRates({
        meetingId: meeting.id,
        requestBody: {
          tutor_rates: changedTutors.map((meetingTutor) => ({
            meeting_tutor_id: meetingTutor.id,
            rate_tutor_paid_at: parseRateToCents(
              tutorRateInputs[meetingTutor.id]
            ) as number,
          })),
          student_rates: changedStudents.map((meetingStudent) => ({
            meeting_student_id: meetingStudent.id,
            rate_student_billed_at: parseRateToCents(
              studentRateInputs[meetingStudent.id]
            ) as number,
          })),
          apply_to_all_future_meetings: applyToAllFutureMeetings,
        },
      });

      announceMeetingsChanged();
      const newSavedStudentRates = { ...savedStudentRates };
      changedStudents.forEach((meetingStudent) => {
        newSavedStudentRates[meetingStudent.id] = parseRateToCents(
          studentRateInputs[meetingStudent.id]
        ) as number;
      });
      setSavedStudentRates(newSavedStudentRates);

      const newSavedTutorRates = { ...savedTutorRates };
      changedTutors.forEach((meetingTutor) => {
        newSavedTutorRates[meetingTutor.id] = parseRateToCents(
          tutorRateInputs[meetingTutor.id]
        ) as number;
      });
      setSavedTutorRates(newSavedTutorRates);

      // Tell the admin which meetings the update left untouched because
      // billing already locked them.
      if (response.skipped_locked_meetings.length > 0) {
        const skippedMeetingDates = response.skipped_locked_meetings
          .map((skippedMeeting) => moment(skippedMeeting.start))
          .sort((a, b) => a.valueOf() - b.valueOf())
          .map((skippedStart) => skippedStart.format("ddd, MMM Do"));

        Notifications.info(
          "The following meetings were skipped as they have already been " +
            `locked for billing: ${skippedMeetingDates.join(", ")}.`
        );
      }

      setSaveStatus(PageStatus.SUCCESS);
      setEvents();
    } catch (error: any) {
      // The endpoint applies everything in one transaction, so a failure
      // means no rates were changed.
      setSaveStatus(PageStatus.ERROR);
      Notifications.error(
        (error?.message || "Error updating rates") + ". No changes were saved."
      );
      setConfirmOpen(false);
    }
  }

  function closeConfirmDialog() {
    setConfirmOpen(false);
    setSaveStatus(undefined);
    setChangeType(CHANGE_OPTIONS.JUST_THIS_SESSION);
  }

  function rateRow({
    key,
    firstName,
    lastName,
    statusLabel,
    inputValue,
    savedRateCents,
    invalid,
    onInputChange,
  }: {
    key: number;
    firstName: string;
    lastName: string;
    statusLabel: string;
    inputValue: string | undefined;
    savedRateCents: number;
    invalid: boolean;
    onInputChange: (value: string | undefined) => void;
  }) {
    const parsedCents = parseRateToCents(inputValue);
    const amountCents =
      parsedCents !== undefined ? parsedCents : savedRateCents;

    return (
      <div
        key={key}
        className="flex items-center justify-between gap-3 px-3 py-2.5"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-blue-600 font-semibold text-xs">
              {firstName[0]}
              {lastName[0]}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {firstName} {lastName}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {statusLabel} · $
              {meeting
                ? formatAmountForMeeting(amountCents, meeting.duration)
                : centsToDollarsString(amountCents)}
            </p>
          </div>
        </div>

        <div
          className={`flex items-center gap-1 rounded-md border bg-white px-2 flex-shrink-0 focus-within:border-green-500 focus-within:ring-1 focus-within:ring-green-500 ${
            invalid ? "border-red-400" : "border-gray-300"
          }`}
        >
          <span className="text-xs text-gray-400">$</span>
          <CurrencyInput
            defaultValue={centsToDollarsString(savedRateCents)}
            decimalsLimit={2}
            allowNegativeValue={false}
            onValueChange={(value) => onInputChange(value)}
            disabled={locked}
            className="w-16 border-0 bg-transparent p-1 text-sm text-right text-gray-900 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:text-gray-400"
          />
          <span className="text-xs text-gray-400">/hr</span>
        </div>
      </div>
    );
  }

  return (
    <DialogContent className="dialog-content dialog-content--left medium bg-neutral-50 font-montserrat">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-200 bg-white">
          <h2 className="text-xl font-bold text-gray-900">
            Approval &amp; Billing
            {locked && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 align-middle">
                <LockClosedIcon width={11} height={11} />
                Locked
              </span>
            )}
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            {meetingName
              ? `${meetingName} · ${meetingTimeString}`
              : "Manage this meeting's approval status and rates"}
          </p>
        </div>

        {status === PageStatus.LOADING && (
          <div className="flex-1 px-5 py-10 text-center">
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </div>
        )}

        {status === PageStatus.ERROR && (
          <div className="flex-1 px-5 py-10 text-center text-sm text-gray-600">
            Unable to load meeting billing details. Please close this window and
            try again.
          </div>
        )}

        {status === PageStatus.SUCCESS && meeting && (
          <>
            {/* Content */}
            <div className="flex-1 px-5 py-4 overflow-y-auto space-y-5">
              {/* Admin approval */}
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Admin Approval
                </h3>
                <div
                  className={`inline-flex rounded-lg bg-gray-100 p-0.5 ${
                    locked ? "opacity-60" : ""
                  }`}
                >
                  {APPROVAL_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => changeAdminApproval(option.value)}
                      disabled={locked || savingApproval}
                      className={`px-3 py-1 text-sm rounded-md transition-colors ${
                        locked ? "cursor-not-allowed" : ""
                      } ${
                        adminApproval === option.value
                          ? `font-semibold ${option.activeClasses}`
                          : "font-medium text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Student rates */}
              {meetingStudents.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    Student Rates
                  </h3>
                  <div
                    className={`bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 ${
                      locked ? "opacity-60" : ""
                    }`}
                  >
                    {meetingStudents.map((meetingStudent) =>
                      rateRow({
                        key: meetingStudent.id,
                        firstName: meetingStudent.student.first_name,
                        lastName: meetingStudent.student.last_name,
                        statusLabel:
                          BILLING_STATUS_LABELS[meetingStudent.student_billed],
                        inputValue: studentRateInputs[meetingStudent.id],
                        savedRateCents:
                          savedStudentRates[meetingStudent.id] ?? 0,
                        invalid:
                          invalidStudentRates[meetingStudent.id] ?? false,
                        onInputChange: (value) => {
                          setStudentRateInputs((previous) => ({
                            ...previous,
                            [meetingStudent.id]: value,
                          }));
                          const { invalid, message } = verifyRateEdit(value);
                          setInvalidStudentRates((previous) => ({
                            ...previous,
                            [meetingStudent.id]: invalid,
                          }));
                          if (invalid && message) {
                            Notifications.error(message);
                          }
                        },
                      })
                    )}
                  </div>
                </div>
              )}

              {/* Tutor rates */}
              {meetingTutors.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                    Tutor Rates
                  </h3>
                  <div
                    className={`bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 ${
                      locked ? "opacity-60" : ""
                    }`}
                  >
                    {meetingTutors.map((meetingTutor) =>
                      rateRow({
                        key: meetingTutor.id,
                        firstName: meetingTutor.tutor.first_name,
                        lastName: meetingTutor.tutor.last_name,
                        statusLabel:
                          PAYMENT_STATUS_LABELS[meetingTutor.tutor_paid],
                        inputValue: tutorRateInputs[meetingTutor.id],
                        savedRateCents: savedTutorRates[meetingTutor.id] ?? 0,
                        invalid: invalidTutorRates[meetingTutor.id] ?? false,
                        onInputChange: (value) => {
                          setTutorRateInputs((previous) => ({
                            ...previous,
                            [meetingTutor.id]: value,
                          }));
                          const { invalid, message } = verifyRateEdit(value);
                          setInvalidTutorRates((previous) => ({
                            ...previous,
                            [meetingTutor.id]: invalid,
                          }));
                          if (invalid && message) {
                            Notifications.error(message);
                          }
                        },
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer with Save Button */}
            <div className="px-5 py-3 border-t border-gray-200 bg-white flex justify-end">
              <Button
                size={ButtonSize.SMALL}
                color={canSave ? ButtonColor.GREEN : ButtonColor.GRAY}
                onClick={() => canSave && setConfirmOpen(true)}
                disabled={!canSave}
              >
                {locked ? "Meeting Locked" : "Save Rate Changes"}
              </Button>
            </div>

            {/* Confirmation dialog for rate changes */}
            <Dialog
              open={confirmOpen}
              onOpenChange={(open) => {
                if (!open) {
                  closeConfirmDialog();
                } else {
                  setConfirmOpen(open);
                }
              }}
            >
              {confirmOpen &&
                (saveStatus === PageStatus.SUCCESS ? (
                  <SuccessDialog
                    message="Changes Saved!"
                    onClose={closeConfirmDialog}
                  />
                ) : (
                  <DialogContent
                    className="dialog-content dialog-content--left wide h-fit max-h-[85vh] lg:max-w-[33vw] md:w-1/2 w-3/4 bg-neutral-50 font-montserrat pb-4 overflow-scroll"
                    alignLeft={true}
                    showClose={false}
                  >
                    <div className="meeting-dialog-new--title pt-4">
                      <span>Save Changes?</span>
                    </div>

                    <p className="font-bold text-md border-b-2 pb-2 text-green-500">
                      Rate Changes
                    </p>
                    <div className="flex flex-col text-sm mt-2 space-y-1">
                      {changedStudents.map((meetingStudent) => (
                        <span key={`student-${meetingStudent.id}`}>
                          <b>
                            {meetingStudent.student.first_name}{" "}
                            {meetingStudent.student.last_name}
                          </b>{" "}
                          (student): $
                          {centsToDollarsString(
                            savedStudentRates[meetingStudent.id]
                          )}
                          /hr &rarr; $
                          {centsToDollarsString(
                            parseRateToCents(
                              studentRateInputs[meetingStudent.id]
                            ) as number
                          )}
                          /hr
                        </span>
                      ))}
                      {changedTutors.map((meetingTutor) => (
                        <span key={`tutor-${meetingTutor.id}`}>
                          <b>
                            {meetingTutor.tutor.first_name}{" "}
                            {meetingTutor.tutor.last_name}
                          </b>{" "}
                          (tutor): $
                          {centsToDollarsString(
                            savedTutorRates[meetingTutor.id]
                          )}
                          /hr &rarr; $
                          {centsToDollarsString(
                            parseRateToCents(
                              tutorRateInputs[meetingTutor.id]
                            ) as number
                          )}
                          /hr
                        </span>
                      ))}
                    </div>

                    <div className="mt-4">
                      <div className="font-bold mb-2 text-md">
                        Apply rate changes to:
                      </div>
                      <div className="flex flex-row items-center gap-2 mb-2">
                        <RadioInput
                          id={CHANGE_OPTIONS.JUST_THIS_SESSION}
                          name={CHANGE_OPTIONS.JUST_THIS_SESSION}
                          value={CHANGE_OPTIONS.JUST_THIS_SESSION}
                          label="Just this meeting"
                          setValue={(e) => setChangeType(e.target.value)}
                          checked={
                            changeType === CHANGE_OPTIONS.JUST_THIS_SESSION
                          }
                          required
                        />
                      </div>
                      <div className="flex flex-row items-center gap-2 mb-2">
                        <RadioInput
                          id={CHANGE_OPTIONS.ALL_FUTURE_OCCURRENCES}
                          name={CHANGE_OPTIONS.ALL_FUTURE_OCCURRENCES}
                          value={CHANGE_OPTIONS.ALL_FUTURE_OCCURRENCES}
                          label="All future occurrences of this meeting"
                          setValue={(e) => setChangeType(e.target.value)}
                          checked={
                            changeType === CHANGE_OPTIONS.ALL_FUTURE_OCCURRENCES
                          }
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <DialogActions>
                        <Button
                          size={ButtonSize.SMALL}
                          color={
                            saveStatus === PageStatus.LOADING
                              ? ButtonColor.GRAY
                              : ButtonColor.GREEN
                          }
                          fill={ButtonFill.DEFAULT}
                          disabled={saveStatus === PageStatus.LOADING}
                          onClick={saveRateChanges}
                        >
                          {saveStatus === PageStatus.LOADING ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            <>Confirm</>
                          )}
                        </Button>
                        <DialogClose asChild>
                          <Button
                            size={ButtonSize.SMALL}
                            color={ButtonColor.BLACK}
                            fill={ButtonFill.HOLLOW}
                          >
                            Back
                          </Button>
                        </DialogClose>
                      </DialogActions>
                    </div>
                  </DialogContent>
                ))}
            </Dialog>
          </>
        )}
      </div>
    </DialogContent>
  );
}
